import useGlobalStore from '@/store/useGlobalStore'
import { useMemo } from 'react'

type UseOrderLimitCheckProps = {
  limit: number | null // null means unlimited
  currentCount: number // how many are already in cart (or in scope)
  incrementAmount?: number // how many the user wants to add (default 1)
  sku: string // the SKU of the product being added
}

export function useOrderLimitCheck({
  limit,
  currentCount,
  incrementAmount = 1,
  sku,
}: UseOrderLimitCheckProps) {
  const { cart } = useGlobalStore()
  // Find a matching OTP item by SKU
  let adjustedCount = currentCount
  let cartQuantity = 0
  if (cart?.otp && Array.isArray(cart.otp)) {
    const found = cart.otp.find((item) => {
      if (!item || typeof item !== 'object') return false
      return (
        'product' in item &&
        typeof item.product === 'object' &&
        item.product !== null &&
        'sku' in item.product &&
        item.product.sku === sku
      )
    })

    if (found && 'qty' in found) {
      cartQuantity = Number(found.qty) || 0
      adjustedCount += cartQuantity
    }
  }

  return useMemo(() => {
    if (limit == null) {
      // No limit
      return {
        isLimitReached: false,
        remaining: null,
        limit: null,
        currentCount: adjustedCount,
        isLimitAlreadyInCart: false,
        cartQuantity,
      }
    }

    const total = adjustedCount + incrementAmount
    const isLimitReached = total > limit
    const remaining = Math.max(0, limit - cartQuantity)
    const isLimitAlreadyInCart = cartQuantity >= limit

    return {
      isLimitReached,
      remaining,
      limit,
      currentCount: adjustedCount,
      isLimitAlreadyInCart,
      cartQuantity,
    }
  }, [limit, adjustedCount, incrementAmount, cartQuantity])
}
